import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, Check, Info, Users, Calendar, MessageCircle } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Loader } from '../../components/ui/Loader';
import { Modal } from '../../components/ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { useSchoolRegistration, useActiveEvents, useRegistrationMutations } from '../../hooks/useConvex';
import { Student } from '../../types';
import { Id } from '../../../convex/_generated/dataModel';

// Type for event data from Convex
type ConvexEvent = {
    _id: Id<'events'>;
    name: string;
    day?: 'Day 1' | 'Day 2' | 'Both';
    schedule?: { day1?: { start: string; end: string }; day2?: { start: string; end: string } };
    max_participants?: number;
    [key: string]: unknown;
};

const parseTime = (timeStr: string) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
};

const hasTimeConflict = (event1: { day?: string; schedule?: { day1?: { start: string; end: string }; day2?: { start: string; end: string } } }, event2: { day?: string; schedule?: { day1?: { start: string; end: string }; day2?: { start: string; end: string } } }) => {
    const days1 = event1.day === 'Both' ? ['Day 1', 'Day 2'] : [event1.day || 'Day 1'];
    const days2 = event2.day === 'Both' ? ['Day 1', 'Day 2'] : [event2.day || 'Day 1'];
    const commonDays = days1.filter(d => days2.includes(d));

    if (commonDays.length === 0) return false;

    for (const day of commonDays) {
        const schedule1 = day === 'Day 1' ? event1.schedule?.day1 : event1.schedule?.day2;
        const schedule2 = day === 'Day 1' ? event2.schedule?.day1 : event2.schedule?.day2;

        if (!schedule1?.start || !schedule1?.end || !schedule2?.start || !schedule2?.end) continue;

        const start1 = parseTime(schedule1.start);
        const end1 = parseTime(schedule1.end);
        const start2 = parseTime(schedule2.start);
        const end2 = parseTime(schedule2.end);

        // Gap is sufficient if: start2 >= end1 + 120 OR start1 >= end2 + 120
        const sufficientGap = (start2 >= end1 + 120) || (start1 >= end2 + 120);

        if (!sufficientGap) return true;
    }

    return false;
};

export const RegisterParticipantsPage: React.FC = () => {
    const { school } = useAuth();
    const registrationData = useSchoolRegistration(school?._id as Id<'schools'> | undefined);
    const eventsData = useActiveEvents();
    const { createDraft, update, submit } = useRegistrationMutations();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingStudentIndex, setEditingStudentIndex] = useState<number | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [viewMode, setViewMode] = useState<'by_student' | 'by_event'>('by_event');

    // Event Management Modal State
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState<ConvexEvent | null>(null);
    const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);

    // Form State
    const [studentForm, setStudentForm] = useState<Partial<Student>>({
        name: '',
        grade: '',
        contact: '',
        email: '',
        events: []
    });

    const loading = registrationData === undefined || eventsData === undefined;
    const registration = registrationData;
    const events = eventsData?.filter(e => e.is_active) || [];

    // Create draft registration if not exists
    useEffect(() => {
        const initializeDraft = async () => {
            if (school?._id && registrationData === null) {
                try {
                    await createDraft({ schoolId: school._id as Id<'schools'> });
                } catch (error) {
                    console.error('Error creating draft:', error);
                }
            }
        };
        initializeDraft();
    }, [school?._id, registrationData, createDraft]);

    const handleAddStudent = () => {
        setStudentForm({
            name: '',
            grade: '',
            contact: '',
            email: '',
            events: []
        });
        setEditingStudentIndex(null);
        setIsModalOpen(true);
    };

    const handleEditStudent = (student: Student, index: number) => {
        setStudentForm({ ...student });
        setEditingStudentIndex(index);
        setIsModalOpen(true);
    };

    const handleSaveStudent = async () => {
        if (!registration || !studentForm.name || !studentForm.grade) {
            alert('Please fill in required fields');
            return;
        }

        try {
            setActionLoading(true);
            const updatedStudents = [...(registration.students || [])];

            const newStudent: Student = {
                serial_number: editingStudentIndex !== null ? updatedStudents[editingStudentIndex].serial_number : updatedStudents.length + 1,
                name: studentForm.name!,
                grade: studentForm.grade!,
                contact: studentForm.contact || '',
                email: studentForm.email || '',
                photo_url: studentForm.photo_url || '',
                events: studentForm.events || []
            };

            if (editingStudentIndex !== null) {
                updatedStudents[editingStudentIndex] = newStudent;
            } else {
                updatedStudents.push(newStudent);
            }

            await update({
                registrationId: registration._id as Id<'registrations'>,
                students: updatedStudents,
                totalParticipants: updatedStudents.length
            });

            setIsModalOpen(false);
        } catch (error: any) {
            alert(error.message || 'Failed to save student');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteStudent = async (index: number) => {
        if (!registration || !window.confirm('Are you sure you want to remove this student?')) return;

        try {
            const updatedStudents = registration.students.filter((_, i) => i !== index);

            await update({
                registrationId: registration._id as Id<'registrations'>,
                students: updatedStudents,
                totalParticipants: updatedStudents.length
            });
        } catch (error) {
            console.error('Error deleting student:', error);
        }
    };

    const toggleEventSelection = (eventId: string) => {
        const currentEvents = studentForm.events || [];
        if (currentEvents.includes(eventId)) {
            setStudentForm({ ...studentForm, events: currentEvents.filter(id => id !== eventId) });
        } else {
            // Check for conflicts
            const targetEvent = events.find(e => e._id === eventId);
            if (targetEvent) {
                const conflictingEventId = currentEvents.find(existingId => {
                    const existingEvent = events.find(e => e._id === existingId);
                    return existingEvent && hasTimeConflict(targetEvent, existingEvent);
                });

                if (conflictingEventId) {
                    const conflictingEvent = events.find(e => e._id === conflictingEventId);
                    alert(`Time conflict with "${conflictingEvent?.name}". Events must have a gap of at least 2 hours.`);
                    return;
                }
            }
            setStudentForm({ ...studentForm, events: [...currentEvents, eventId] });
        }
    };

    const getConflictingEvent = (targetEventId: string, currentEventIds: string[]) => {
        const targetEvent = events.find(e => e._id === targetEventId);
        if (!targetEvent) return null;

        const conflictingId = currentEventIds.find(existingId => {
            if (existingId === targetEventId) return false;
            const existingEvent = events.find(e => e._id === existingId);
            return existingEvent && hasTimeConflict(targetEvent, existingEvent);
        });

        return conflictingId ? events.find(e => e._id === conflictingId) : null;
    };

    const handleSubmitRegistration = async () => {
        if (!registration) return;
        if (!window.confirm('Are you sure you want to submit your registration? You will not be able to make changes until it is approved or rejected.')) return;

        try {
            setActionLoading(true);
            await submit({ registrationId: registration._id as Id<'registrations'> });
            alert('Registration submitted successfully!');
        } catch (error: any) {
            alert(error.message || 'Failed to submit registration');
        } finally {
            setActionLoading(false);
        }
    };

    // --- By Event View Logic ---

    const handleManageEventParticipants = (event: ConvexEvent) => {
        if (!registration) return;
        setSelectedEvent(event);
        // Find students currently registered for this event
        const registeredIndices = registration.students
            .map((s, idx) => s.events.includes(event._id as string) ? idx : -1)
            .filter(idx => idx !== -1);
        setSelectedStudentIds(registeredIndices);
        setIsEventModalOpen(true);
    };

    const handleSaveEventParticipants = async () => {
        if (!registration || !selectedEvent) return;

        try {
            setActionLoading(true);
            const updatedStudents = [...registration.students];

            // Update students based on selection
            updatedStudents.forEach((student, index) => {
                const hasEvent = student.events.includes(selectedEvent._id as string);
                const isSelected = selectedStudentIds.includes(index);

                if (isSelected && !hasEvent) {
                    // Add event
                    student.events = [...student.events, selectedEvent._id as string];
                } else if (!isSelected && hasEvent) {
                    // Remove event
                    student.events = student.events.filter(id => id !== selectedEvent._id);
                }
            });

            await update({
                registrationId: registration._id as Id<'registrations'>,
                students: updatedStudents,
                totalParticipants: updatedStudents.length
            });

            setIsEventModalOpen(false);
            setSelectedEvent(null);
        } catch (error: any) {
            alert(error.message || 'Failed to update event participants');
        } finally {
            setActionLoading(false);
        }
    };

    const toggleStudentForEvent = (studentIndex: number) => {
        if (selectedStudentIds.includes(studentIndex)) {
            setSelectedStudentIds(prev => prev.filter(id => id !== studentIndex));
        } else {
            // Check max team size
            if (selectedEvent?.max_participants && selectedStudentIds.length >= selectedEvent.max_participants) {
                alert(`Maximum team size for this event is ${selectedEvent.max_participants}`);
                return;
            }

            // Check for conflicts with student's existing events
            if (selectedEvent && registration) {
                const student = registration.students[studentIndex];
                const conflictingEventId = student.events.find(existingId => {
                    const existingEvent = events.find(e => e._id === existingId);
                    return existingEvent && hasTimeConflict(selectedEvent, existingEvent);
                });

                if (conflictingEventId) {
                    const conflictingEvent = events.find(e => e._id === conflictingEventId);
                    alert(`Student ${student.name} has a time conflict with "${conflictingEvent?.name}".`);
                    return;
                }
            }

            setSelectedStudentIds(prev => [...prev, studentIndex]);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-[60vh]">
                <Loader size="lg" />
            </div>
        );
    }

    const isEditable = registration?.status === 'draft' || registration?.status === 'rejected';

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-4xl font-heading font-bold text-white mb-2 tracking-tight">
                        Participant <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF991C] to-[#FFB84D]">Registration</span>
                    </h1>
                    <p className="text-gray-400 text-lg">
                        Manage your team roster and event assignments
                    </p>
                </div>
                <div className="flex gap-3">
                    {isEditable && (
                        <>
                            <Button variant="outline" onClick={handleAddStudent} className="hover:bg-white/5">
                                <Plus className="w-4 h-4 mr-2" />
                                Add Student
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmitRegistration}
                                isLoading={actionLoading}
                                disabled={!registration?.students?.length}
                                className="transition-shadow"
                            >
                                <Check className="w-4 h-4 mr-2" />
                                Submit Registration
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Status Banner */}
            {!isEditable && (
                <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                    <div className="flex items-center gap-3">
                        <Info className="w-5 h-5 text-yellow-500" />
                        <p className="text-yellow-500 font-medium">
                            Registration is currently {registration?.status}. You cannot make changes at this time.
                        </p>
                    </div>
                    {registration?.status === 'submitted' && (
                        <div className="mt-3 p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-3">
                            <MessageCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <div>
                                <p className="text-sm text-gray-300">
                                    Need to make changes? Contact us on WhatsApp:
                                </p>
                                <a
                                    href="https://wa.me/919452900015"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-400 font-bold hover:text-green-300 transition-colors"
                                >
                                    +91 9452900015
                                </a>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* View Tabs */}
            <div className="flex gap-2 md:gap-4 border-b border-white/10 overflow-x-auto">
                <button
                    onClick={() => setViewMode('by_student')}
                    className={`pb-4 px-4 text-sm font-bold font-heading tracking-wide transition-all relative ${viewMode === 'by_student' ? 'text-brand' : 'text-gray-400 hover:text-white'
                        }`}
                >
                    <div className="flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        By Student
                    </div>
                    {viewMode === 'by_student' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand" />
                    )}
                </button>
                <button
                    onClick={() => setViewMode('by_event')}
                    className={`pb-4 px-4 text-sm font-bold font-heading tracking-wide transition-all relative ${viewMode === 'by_event' ? 'text-brand-light' : 'text-gray-400 hover:text-white'
                        }`}
                >
                    <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        By Event
                    </div>
                    {viewMode === 'by_event' && (
                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-light" />
                    )}
                </button>
            </div>

            {/* Content */}
            {viewMode === 'by_student' ? (
                <Card className="overflow-hidden border-white/5 bg-black/40 backdrop-blur-xl relative">
                    <div className="absolute inset-0 bg-gradient-to-b from-white/5 to-transparent pointer-events-none" />
                    <div className="overflow-x-auto relative z-10">
                        <table className="w-full">
                            <thead className="bg-white/5 border-b border-white/10">
                                <tr>
                                    <th className="px-6 py-5 text-left text-xs font-heading font-bold text-brand uppercase tracking-widest">Student Name</th>
                                    <th className="px-6 py-5 text-left text-xs font-heading font-bold text-gray-400 uppercase tracking-widest">Grade</th>
                                    <th className="px-6 py-5 text-left text-xs font-heading font-bold text-brand-light uppercase tracking-widest">Events</th>
                                    <th className="px-6 py-5 text-right text-xs font-heading font-bold text-gray-400 uppercase tracking-widest">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {!registration?.students?.length ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-16 text-center text-gray-500">
                                            No students added yet. Click "Add Student" to get started.
                                        </td>
                                    </tr>
                                ) : (
                                    registration.students.map((student, index) => (
                                        <tr key={index} className="hover:bg-white/5 transition-all duration-300 group">
                                            <td className="px-6 py-5">
                                                <div className="font-bold text-white group-hover:text-brand transition-colors">{student.name}</div>
                                                <div className="text-xs text-gray-500">{student.email}</div>
                                            </td>
                                            <td className="px-6 py-5 text-gray-400">{student.grade}</td>
                                            <td className="px-6 py-5">
                                                <div className="flex flex-wrap gap-2">
                                                    {student.events.map(eventId => {
                                                        const event = events.find(e => e._id === eventId);
                                                        return event ? (
                                                            <Badge key={eventId} variant="neutral" size="sm" className="bg-white/5 border-white/10">
                                                                {event.name}
                                                            </Badge>
                                                        ) : null;
                                                    })}
                                                </div>
                                            </td>
                                            <td className="px-6 py-5 text-right">
                                                {isEditable && (
                                                    <div className="flex justify-end gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleEditStudent(student, index)}
                                                            className="text-gray-400 hover:text-white"
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDeleteStudent(index)}
                                                            className="text-gray-400 hover:text-red-500"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>
            ) : (
                <div className="space-y-8">
                    {/* Day 1 Section */}
                    <div>
                        <div className="flex items-center gap-4 mb-6">
                            <div className="h-px bg-white/10 flex-1" />
                            <span className="text-brand font-heading font-bold uppercase tracking-widest text-sm">Day 1 Events</span>
                            <div className="h-px bg-white/10 flex-1" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {events.filter(e => e.day === 'Day 1' || e.day === 'Both' || !e.day).map(event => {
                                const registeredStudents = registration?.students.filter(s => s.events.includes(event._id as string)) || [];
                                const isFull = event.max_participants ? registeredStudents.length >= event.max_participants : false;

                                return (
                                    <Card key={event._id} className="border-white/5 bg-black/40 backdrop-blur-xl hover:border-brand/30 transition-all duration-300 group">
                                        <div className="p-5 space-y-4">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h3 className="text-lg font-heading font-bold text-white group-hover:text-brand transition-colors">{event.name}</h3>
                                                    <div className="flex gap-2 mt-1">
                                                        <p className="text-xs text-gray-400 uppercase tracking-wider">{event.category}</p>
                                                        {event.day === 'Both' && <Badge variant="warning" size="sm" className="text-[10px] py-0 h-4">Both Days</Badge>}
                                                    </div>
                                                </div>
                                                <Badge variant={isFull ? 'error' : 'neutral'} size="sm">
                                                    {registeredStudents.length} / {event.max_participants || '∞'}
                                                </Badge>
                                            </div>

                                            <div className="min-h-[100px] bg-white/5 rounded-lg p-3 space-y-2">
                                                {registeredStudents.length === 0 ? (
                                                    <p className="text-gray-500 text-sm text-center py-4">No participants yet</p>
                                                ) : (
                                                    registeredStudents.map((student, idx) => (
                                                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-brand" />
                                                            {student.name}
                                                        </div>
                                                    ))
                                                )}
                                            </div>

                                            {isEditable && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    fullWidth
                                                    onClick={() => handleManageEventParticipants(event)}
                                                    className="hover:bg-brand/10 hover:text-brand hover:border-brand/50"
                                                >
                                                    <Users className="w-4 h-4 mr-2" />
                                                    Manage Participants
                                                </Button>
                                            )}
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>

                    {/* Day 2 Section */}
                    <div>
                        <div className="flex items-center gap-4 mb-6">
                            <div className="h-px bg-white/10 flex-1" />
                            <span className="text-brand-light font-heading font-bold uppercase tracking-widest text-sm">Day 2 Events</span>
                            <div className="h-px bg-white/10 flex-1" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {events.filter(e => e.day === 'Day 2').map(event => {
                                const registeredStudents = registration?.students.filter(s => s.events.includes(event._id as string)) || [];
                                const isFull = event.max_participants ? registeredStudents.length >= event.max_participants : false;

                                return (
                                    <Card key={event._id} className="border-white/5 bg-black/40 backdrop-blur-xl hover:border-brand/30 transition-all duration-300 group">
                                        <div className="p-5 space-y-4">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h3 className="text-lg font-heading font-bold text-white group-hover:text-brand transition-colors">{event.name}</h3>
                                                    <p className="text-xs text-gray-400 uppercase tracking-wider mt-1">{event.category}</p>
                                                </div>
                                                <Badge variant={isFull ? 'error' : 'neutral'} size="sm">
                                                    {registeredStudents.length} / {event.max_participants || '∞'}
                                                </Badge>
                                            </div>

                                            <div className="min-h-[100px] bg-white/5 rounded-lg p-3 space-y-2">
                                                {registeredStudents.length === 0 ? (
                                                    <p className="text-gray-500 text-sm text-center py-4">No participants yet</p>
                                                ) : (
                                                    registeredStudents.map((student, idx) => (
                                                        <div key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-brand" />
                                                            {student.name}
                                                        </div>
                                                    ))
                                                )}
                                            </div>

                                            {isEditable && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    fullWidth
                                                    onClick={() => handleManageEventParticipants(event)}
                                                    className="hover:bg-brand/10 hover:text-brand hover:border-brand/50"
                                                >
                                                    <Users className="w-4 h-4 mr-2" />
                                                    Manage Participants
                                                </Button>
                                            )}
                                        </div>
                                    </Card>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )
            }

            {/* Add/Edit Student Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editingStudentIndex !== null ? "Edit Student" : "Add Student"}
            >
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Input
                            label="Full Name"
                            value={studentForm.name}
                            onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })}
                            placeholder="e.g., John Doe"
                            fullWidth
                        />
                        <Input
                            label="Grade/Class"
                            value={studentForm.grade}
                            onChange={(e) => setStudentForm({ ...studentForm, grade: e.target.value })}
                            placeholder="e.g., 11-A"
                            fullWidth
                        />
                        <Input
                            label="Email (Optional)"
                            value={studentForm.email}
                            onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                            placeholder="student@example.com"
                            type="email"
                            fullWidth
                        />
                        <Input
                            label="Contact Number (Optional)"
                            value={studentForm.contact}
                            onChange={(e) => setStudentForm({ ...studentForm, contact: e.target.value })}
                            placeholder="+91 98765 43210"
                            fullWidth
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-400 mb-3">Select Events</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
                            {events.map(event => {
                                const isSelected = studentForm.events?.includes(event._id as string);
                                const conflictingEvent = !isSelected ? getConflictingEvent(event._id as string, studentForm.events || []) : null;
                                const isDisabled = !!conflictingEvent;

                                return (
                                    <div
                                        key={event._id}
                                        onClick={() => !isDisabled && toggleEventSelection(event._id as string)}
                                        className={`p-3 rounded-lg border transition-all relative group ${isSelected
                                            ? 'bg-brand/10 border-brand text-white cursor-pointer'
                                            : isDisabled
                                                ? 'bg-white/5 border-white/5 text-gray-600 cursor-not-allowed opacity-50'
                                                : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 cursor-pointer'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-sm">{event.name}</span>
                                            {isSelected && (
                                                <Check className="w-4 h-4 text-brand" />
                                            )}
                                            {isDisabled && (
                                                <Info className="w-4 h-4 text-red-500" />
                                            )}
                                        </div>
                                        <p className="text-xs opacity-60 mt-1 capitalize">{event.category}</p>

                                        {isDisabled && (
                                            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10 p-2 text-center">
                                                <p className="text-xs text-red-400 font-bold">
                                                    Conflict with {conflictingEvent?.name}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                        <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSaveStudent}
                            isLoading={actionLoading}
                            disabled={!studentForm.name || !studentForm.grade}
                        >
                            <Save className="w-4 h-4 mr-2" />
                            Save Student
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Manage Event Participants Modal */}
            <Modal
                isOpen={isEventModalOpen}
                onClose={() => {
                    setIsEventModalOpen(false);
                    setSelectedEvent(null);
                }}
                title={`Manage Participants - ${selectedEvent?.name}`}
            >
                <div className="space-y-6">
                    {selectedEvent && (
                        <>
                            <div className="flex items-center gap-3 p-3 rounded-lg bg-brand/10 border border-brand/20 text-brand">
                                <Info className="w-5 h-5" />
                                <div className="text-sm">
                                    <p className="font-bold">Team Size Limit: {selectedEvent.max_participants || 'Unlimited'}</p>
                                    <p>Selected: {selectedStudentIds.length}</p>
                                </div>
                            </div>

                            <div className="max-h-[400px] overflow-y-auto custom-scrollbar space-y-2">
                                {registration?.students.length === 0 ? (
                                    <p className="text-gray-500 text-center py-8">No students added yet. Add students first.</p>
                                ) : (
                                    registration?.students.map((student, index) => {
                                        const isSelected = selectedStudentIds.includes(index);

                                        // Check conflict if not selected
                                        let conflictingEventName: string | null | undefined = null;
                                        if (!isSelected && selectedEvent) {
                                            const conflictingEventId = student.events.find(existingId => {
                                                const existingEvent = events.find(e => e._id === existingId);
                                                return existingEvent && hasTimeConflict(selectedEvent, existingEvent);
                                            });
                                            if (conflictingEventId) {
                                                conflictingEventName = events.find(e => e._id === conflictingEventId)?.name;
                                            }
                                        }

                                        const isDisabled = !!conflictingEventName;

                                        return (
                                            <div
                                                key={index}
                                                onClick={() => !isDisabled && toggleStudentForEvent(index)}
                                                className={`p-3 rounded-lg border transition-all flex items-center justify-between ${isSelected
                                                    ? 'bg-brand/10 border-brand text-white cursor-pointer'
                                                    : isDisabled
                                                        ? 'bg-white/5 border-white/5 text-gray-600 cursor-not-allowed opacity-50'
                                                        : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 cursor-pointer'
                                                    }`}
                                            >
                                                <div>
                                                    <p className="font-bold">{student.name}</p>
                                                    <p className="text-xs opacity-70">{student.grade}</p>
                                                    {isDisabled && (
                                                        <p className="text-[10px] text-red-400 mt-1">Conflict: {conflictingEventName}</p>
                                                    )}
                                                </div>
                                                {isSelected && <Check className="w-4 h-4 text-brand" />}
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                                <Button variant="ghost" onClick={() => setIsEventModalOpen(false)}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    onClick={handleSaveEventParticipants}
                                    isLoading={actionLoading}
                                >
                                    <Save className="w-4 h-4 mr-2" />
                                    Save Changes
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </div >
    );
};
